import { useGame, useLiveGameTime } from "../app/GameContext";
import { developmentUpgradeCost, INSTALLATIONS, type Cost } from "../game-data/catalog";
import { developmentStageLabel } from "../game-data/terminology";
import type { CelestialBody, InstallationType, MaterialId } from "../shared/game-types";
import { ActionButton, Meter, formatDeadline, formatDurationWithReal, formatNumber, labels } from "./GameUi";

const installationIds = Object.keys(INSTALLATIONS) as InstallationType[];

export function BodyDevelopment({ body }: { body: CelestialBody }) {
  const { state, secondsPerGameYear } = useGame();
  const liveGameTime = useLiveGameTime();
  if (!state) return null;

  const development = state.developments[body.id];
  const stockpile = development?.stockpile;
  const level = development?.level ?? body.development;
  const buildableIds = installationIds.filter((id) => {
    const definition = INSTALLATIONS[id];
    if (definition.minimumDevelopment > level || !validSite(body, id)) return false;
    return id !== "colony" || (development?.installations.colony ?? 0) === 0;
  });
  const canBuild = (id: InstallationType) => {
    const definition = INSTALLATIONS[id];
    return Boolean(stockpile)
    && state.player.levels.industry >= definition.requiredIndustry
    && state.player.levels.energy >= definition.requiredEnergy
    && state.player.levels.materials >= definition.requiredMaterials
    && stockpile!.energy >= definition.cost.energy
    && Object.entries(definition.cost.materials).every(([materialId, amount]) => (
      stockpile!.materials[materialId as MaterialId] >= (amount ?? 0)
    ));
  };
  const upgradeCost = level >= 2 && level < 6 && level !== 4 ? developmentUpgradeCost(level + 1) : undefined;
  const canAffordUpgrade = Boolean(stockpile && upgradeCost && stockpile.energy >= upgradeCost.energy
    && Object.entries(upgradeCost.materials).every(([materialId, amount]) => stockpile.materials[materialId as MaterialId] >= (amount ?? 0)));
  const canUpgrade = level >= 2
    && level !== 4
    && level < 6
    && !development?.upgradingTo
    && !development?.constructionQueue.length
    && canAffordUpgrade;

  return (
    <section className="development-console" aria-labelledby={`development-${body.id}`}>
      <div className="section-heading">
        <div><p className="eyebrow">DEVELOPMENT</p><h3 id={`development-${body.id}`}>开发与建设</h3></div>
        <span className="status-chip">{developmentStageLabel(level)}</span>
      </div>

      {development?.upgradingTo && (
        <div className="alert warning">正在升级到 {developmentStageLabel(development.upgradingTo)} · {formatDeadline(development.completesAtGameYear ?? liveGameTime, liveGameTime, secondsPerGameYear)}</div>
      )}
      {development?.constructionQueue.length ? (
        <div className="alert warning" aria-label={`${body.name}在建设施`}>
          <strong>设施建设队列</strong>
          {development.constructionQueue.map((project) => (
            <span key={project.id}>{labels.installations[project.installation]} · {formatDeadline(project.completesAtGameYear, liveGameTime, secondsPerGameYear)}</span>
          ))}
        </div>
      ) : null}

      <details>
        <summary>产能与现有设施</summary>
        <div className="body-actions detail-specs">
          <div>
            <div className="stat-line"><span>稳定产量</span><b>{formatNumber(body.stableYield)} / 游戏年</b></div>
            <div className="stat-line"><span>设施状态</span><b>{formatNumber(development?.condition ?? 100)}%</b></div>
            <Meter value={development?.condition ?? 100} label="设施状态" />
          </div>
          <div className="installation-list" aria-label={`${body.name}设施`}>
            {Object.entries(development?.installations ?? {}).map(([id, count]) => <span key={id}><b>{labels.installations[id as InstallationType]} × {count}</b><InstallationEffectLine installation={id as InstallationType} /></span>)}
            {!Object.keys(development?.installations ?? {}).length && <span className="muted">尚无设施</span>}
          </div>
        </div>
      </details>

      {buildableIds.length ? (
        <div className="installation-build-grid" aria-label="可建设设施">
          {buildableIds.map((id) => {
            const definition = INSTALLATIONS[id];
            return <article className="installation-build-card" key={id}>
              <div><h4>{definition.name}</h4><p>{definition.description}</p></div>
              <InstallationEffectLine installation={id} />
              <small>周期 {formatDurationWithReal(definition.buildYears, secondsPerGameYear)}</small>
              <ResourceCosts body={body} cost={definition.cost} />
              <small>需要 {labels.levels.industry} {definition.requiredIndustry}级 / {labels.levels.energy} {definition.requiredEnergy}级 / {labels.levels.materials} {definition.requiredMaterials}级</small>
              <ActionButton className="button primary full" action={{ type: "BUILD_INSTALLATION", bodyId: body.id, installation: id }} disabled={!canBuild(id)}>建设 {definition.name}</ActionButton>
            </article>;
          })}
        </div>
      ) : <p className="empty-state">先完成轨道勘测，才可在这里建设设施。</p>}

      {upgradeCost && <div><span className="field-label">下一阶段本地投入</span><ResourceCosts body={body} cost={upgradeCost} /></div>}
      <ActionButton action={{ type: "UPGRADE_DEVELOPMENT", bodyId: body.id }} disabled={!canUpgrade}>
        {upgradeLabel(level)}
      </ActionButton>
      <small className="field-hint">设施卡显示状态 100% 时的单座基础年效果；实际维护受环境影响，材料产出受当地资源影响。建设和升级只消耗 {body.name} 的当地库存。</small>
    </section>
  );
}

function InstallationEffectLine({ installation }: { installation: InstallationType }) {
  const definition = INSTALLATIONS[installation];
  return <small className="installation-effect">每座每游戏年：能源 +{formatNumber(definition.energyProduction)} · 维护 −{formatNumber(definition.energyMaintenance)} · 科研 +{formatNumber(definition.researchProduction)} · 工程材料 +{formatNumber(definition.materialProduction)}</small>;
}

function ResourceCosts({ body, cost }: { body: CelestialBody; cost: Cost }) {
  const { state } = useGame();
  if (!state) return null;
  const stockpile = state.developments[body.id]?.stockpile;
  if (!stockpile) return null;
  return (
    <div className="resource-costs" aria-label={`${body.name}建设成本与现有库存`}>
      <CostChip tone="energy" label="能源" need={cost.energy} have={stockpile.energy} />
      {Object.entries(cost.materials).map(([id, amount]) => (
        <CostChip key={id} tone="materials" label={labels.materials[id as MaterialId]} need={amount ?? 0} have={stockpile.materials[id as MaterialId]} />
      ))}
    </div>
  );
}

function CostChip({ tone, label, need, have }: { tone: "energy" | "materials"; label: string; need: number; have: number }) {
  const short = have < need;
  return <span className={`resource-cost tone-${tone} ${short ? "short" : ""}`}><small>{label}</small><b>{formatNumber(need)} / {formatNumber(have)}</b>{short && <em>缺少 {formatNumber(need - have)}</em>}</span>;
}

function validSite(body: CelestialBody, installation: InstallationType) {
  if (installation === "atmosphericCollector") return body.type === "gasGiant" || body.type === "iceGiant" || body.pressureBar >= 1;
  if (installation === "colony") return body.type !== "gasGiant" && body.type !== "iceGiant" && body.suitability >= 2;
  if (installation === "miningOutpost" || installation === "industrialBase") return body.type !== "gasGiant" && body.type !== "iceGiant";
  return true;
}

function upgradeLabel(level: number) {
  if (level < 2) return "先完成轨道勘测";
  if (level === 4) return "从“勘测与殖民”发射殖民船";
  if (level >= 6) return "已达成熟核心世界";
  return `升级到 ${developmentStageLabel(level + 1)}`;
}
